import React from 'react';
import { Badge } from "components/ui/badge";
import { Avatar, AvatarImage, AvatarFallback } from "components/ui/avatar";
import { Card, CardHeader, CardContent, CardFooter } from "components/ui/card";
import { Button } from "components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "components/ui/alert-dialog";
import { Acquisition, DealType } from "shared/types/acquisition";
import {
  BuildingApartmentIcon,
  UsersIcon,
  EyeIcon,
  FileIcon,
  FolderOpenIcon,
  ArchiveIcon,
  MapPinIcon,
  RepeatIcon,
  EnvelopeIcon,
  PencilSimpleIcon,
  DotsThreeVerticalIcon,
  FileTextIcon,
} from "@phosphor-icons/react";
import { useUsers } from "shared/contexts/users.context";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { useSession } from "@clerk/clerk-react";
import { toast } from "react-toastify";
import { applyMask } from "shared/utils/masks";
import { BuildingDTO } from "shared/types/building";
import { useNavigate } from "react-router-dom";
import DocumentGeneratorComponent from "components/documents/documentGeneratorComponent";
import { DocumentCategory } from "components/documents/documentGeneratorModal";
import { FileLabel } from "shared/types/file";

interface AcquisitionCardInnerProps {
  acquisition: Acquisition;
  onClick: () => void;
  onUpdate: () => void;
  isHighlighted?: boolean;
  users: any[];
  session: any;
  onCompleteAcquisition?: (acquisition: Acquisition) => void;
}

interface AcquisitionCardProps {
  acquisition: Acquisition;
  onClick: () => void;
  onUpdate: () => void;
  isHighlighted?: boolean;
  onCompleteAcquisition?: (acquisition: Acquisition) => void;
}

// Inner component that doesn't use context hooks
const AcquisitionCardInner: React.FC<AcquisitionCardInnerProps> = React.memo(({
  acquisition: initialAcquisition,
  onClick,
  onUpdate,
  isHighlighted = false,
  users,
  session,
  onCompleteAcquisition
}) => {
  const navigate = useNavigate();
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const [acquisition, setAcquisition] = React.useState(initialAcquisition);
  const [coverImage, setCoverImage] = React.useState<string | null>(null);
  const [imageLoading, setImageLoading] = React.useState(false);
  const [archiveDialogOpen, setArchiveDialogOpen] = React.useState(false);

  // Update local state only when the acquisition ID changes or phase changes
  React.useEffect(() => {
    setAcquisition(prev => {
      // Only update if it's actually a different acquisition or the phase changed
      if (prev.id !== initialAcquisition.id || 
          prev.acquisitionPhase !== initialAcquisition.acquisitionPhase) {
        return initialAcquisition;
      }
      return prev;
    });
  }, [initialAcquisition.id, initialAcquisition.acquisitionPhase, initialAcquisition]);

  // Fetch cover image directly to avoid HeroUI Image component loading states
  React.useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const fetchCoverImage = async () => {
      if (!acquisition.id) return;
      
      setImageLoading(true);
      try{
        const buildingId = acquisition.buildings?.[0]?.id;
        const queryParams = new URLSearchParams({
          buildingId: buildingId || "",
          unitId: "",
          acquisitionId: acquisition.id?.toString() || "",
          exclusionType: "",
          fileLabel: "",
          secondaryLabel: "",
          page: "1",
          pageSize: "1",
        });

        const response = await fetchWithAuth(
          `${API_URL}/file/get-images?${queryParams}`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ 
              labels: [FileLabel.CoverImage],
              acquisitionId: acquisition.id 
            }),
            signal: controller.signal,
          },
          session
        );

        if (!response.ok) throw new Error("Failed to fetch image");

        const images = await response.json();
        
        if (isMounted && images && images.length > 0) {
          const validImages = images.filter((img: any) => img.imageFound);
          if (validImages.length > 0) {
            setCoverImage(validImages[0].imageUrl);
          } else {
            setCoverImage("https://yally.be/uploads/placeholder.jpg");
          }
        } else if (isMounted) {
          setCoverImage("https://yally.be/uploads/placeholder.jpg");
        }
      } catch (error: any) {
        if (isMounted && error?.name !== 'AbortError') {
          setCoverImage("https://yally.be/uploads/placeholder.jpg");
        }
      } finally {
        if (isMounted) setImageLoading(false);
      }
    };

    fetchCoverImage();

    return () => {
      isMounted = false;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acquisition.id, acquisition.buildings?.length]);

  // Memoize user lookups to prevent re-calculations on users array reference changes
  const getUserImageUrlById = React.useCallback((userId: string) => {
    const foundUser = users.find((user) => user.id === userId);
    return foundUser?.image_url || null;
  }, [users.length]); // Only recreate if user count changes

  const getUserName = React.useCallback((userId: string) => {
    const foundUser = users.find((user) => user.id === userId);
    return foundUser ? `${foundUser.first_name} ${foundUser.last_name}` : "Unknown";
  }, [users.length]); // Only recreate if user count changes

  const refreshAcquisition = async () => {
    setIsRefreshing(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/acquisitions/${acquisition.id}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        },
        session
      );

      if (!response.ok) {
        throw new Error("Failed to refresh acquisition");
      }

      const updatedAcquisition = await response.json();
      // Update only this card's local state
      setAcquisition(updatedAcquisition);
      toast.success("Financiële gegevens ververst!");
    } catch (error) {
      console.error("Error refreshing acquisition:", error);
      toast.error("Kon financiële gegevens niet verversen");
    } finally {
      setIsRefreshing(false);
    }
  };

  const getDealTypeColor = (dealType: DealType): string => {
    switch (dealType) {
      case DealType.ASSET_DEAL:
        return "bg-moss-primary/20 text-moss-primary hover:bg-moss-primary/30";
      case DealType.SHARE_DEAL:
        return "bg-brick-primary/20 text-brick-primary hover:bg-brick-primary/30";
      default:
        return "bg-neutral-200 text-neutral-700 hover:bg-neutral-200/80";
    }
  };

  const getDealTypeLabel = (dealType: DealType) => {
    switch (dealType) {
      case DealType.ASSET_DEAL:
        return "Asset Deal";
      case DealType.SHARE_DEAL:
        return "Share Deal";
      default:
        return "Onbekend";
    }
  };

  const openNewDocumentGenerator = () => {
    const entities: Record<string, any> = {};

    entities.acquisition = {
      type: 'acquisition',
      id: acquisition.id,
      resourceId: acquisition.id,
      name: acquisition.name || `Acquisitie ${acquisition.id}`,
      data: acquisition,
    };

    if (acquisition.buildings && acquisition.buildings.length > 0) {
      acquisition.buildings.forEach((building: BuildingDTO, index: number) => {
        const buildingKey = index === 0 ? 'building' : `building${index + 1}`;
        entities[buildingKey] = {
          type: 'building',
          id: building.id || building.resourceId,
          resourceId: building.resourceId || building.id,
          name: building.googlePlaceLongAddress || `Gebouw ${index + 1}`,
          data: building,
        };
      });
    }

    if (acquisition.owner) {
      entities.owner = {
        type: 'contact',
        id: acquisition.owner.id,
        resourceId: acquisition.owner.resourceId,
        name: acquisition.owner.name || 'Eigenaar',
        data: acquisition.owner,
      };
    }

    if (acquisition.intermediary) {
      entities.intermediary = {
        type: 'contact',
        id: acquisition.intermediary.id,
        resourceId: acquisition.intermediary.resourceId,
        name: acquisition.intermediary.name || 'Makelaar',
        data: acquisition.intermediary,
      };
    }

    const dynamicValues: Record<string, string> = {};
    dynamicValues.datum_vandaag = new Date().toLocaleDateString('nl-BE');

    if (acquisition.yallyResponsibleUser) {
      dynamicValues.verantwoordelijke = `${acquisition.yallyResponsibleUser.first_name || ''} ${acquisition.yallyResponsibleUser.last_name || ''}`.trim();
    }

    const fiscalDiscountRealEstate = Number(acquisition.fiscalDiscountRealEstate) || 0;
    const fiscalDiscountCompanyShares = Number(acquisition.fiscalDiscountCompanyShares) || 0;
    dynamicValues.totale_korting = (fiscalDiscountRealEstate + fiscalDiscountCompanyShares).toString();

    if (acquisition.bidPriceBestCaseNetYield) {
      dynamicValues.rendement_na_korting = acquisition.bidPriceBestCaseNetYield;
    }

    navigate('/document-generator', {
      state: {
        entities,
        category: 'ACQUISITION',
        dynamicValues,
      },
    });
  };

  const archiveAcquisition = async (e?: React.MouseEvent) => {
    e?.stopPropagation();
    try {
      const response = await fetchWithAuth(
        `${API_URL}/acquisitions/${acquisition.id}/archive`,
        {
          headers: { "Content-Type": "application/json" },
          method: "PUT",
        },
        session
      );

      if (!response.ok) {
        toast.error("Archiveren mislukt!");
        throw new Error("Failed to archive acquisition.");
      } else {
        toast.success("Acquisitie gearchiveerd!");
        onUpdate();
      }
    } catch (error) {
      console.error("Error archiving acquisition:", error);
    }
  };

  // Check if acquisition needs completion (email import with no buildings or in INBOX)
  const needsCompletion = acquisition.isEmailImport &&
    (!acquisition.buildings || acquisition.buildings.length === 0);

  const assigneeImage = getUserImageUrlById(acquisition.yallyResponsibleUserId || '');

  const buildingAddresses = acquisition.buildings
    ?.map((building: BuildingDTO) => building.googlePlaceLongAddress)
    .filter(Boolean)
    .join(", ") || "Geen adres";

  const totalUnits = acquisition.buildings?.reduce((sum, building: BuildingDTO) => 
    sum + (Number(building.buildingAcquisitionInfo?.acquisitionFileNumResidentialUnits) || 0), 0
  ) || 0;

  return (
    <Card
      onClick={onClick}
      className={`group cursor-grab w-full max-w-full h-full border-none shadow-sm hover:shadow-xl hover:ring-2 hover:ring-offset-2 hover:ring-moss-primary transition-all duration-300 ${
        isHighlighted 
            ? 'ring-2 ring-offset-2 ring-moss-primary animate-pulse' 
            : ''
    }`}
    >
      <CardHeader className="space-y-2 p-3">
        {/* Header */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            {acquisition.yallyResponsibleUserId && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <Avatar className="h-6 w-6">
                      {assigneeImage ? (
                        <AvatarImage src={assigneeImage} alt={getUserName(acquisition.yallyResponsibleUserId)} />
                      ) : null}
                      <AvatarFallback>
                        <UsersIcon size={12} />
                      </AvatarFallback>
                    </Avatar>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{getUserName(acquisition.yallyResponsibleUserId)}</p>
                </TooltipContent>
              </Tooltip>
            )}
            <Badge variant="outline" className="w-fit text-xs">ACQ-{acquisition.id}</Badge>
            {acquisition.dealType && (
              <Badge className={`text-xs ${getDealTypeColor(acquisition.dealType)}`}>
                {getDealTypeLabel(acquisition.dealType)}
              </Badge>
            )}
            {acquisition.isEmailImport && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant="outline" className="w-fit text-xs">
                    <EnvelopeIcon size={11} className="mr-1" weight="duotone" />
                    Email
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  <div className="max-w-xs">
                    <p className="font-semibold">Van: {acquisition.emailFrom}</p>
                    <p className="text-xs">Ontvangen: {acquisition.emailReceivedAt ? new Date(acquisition.emailReceivedAt).toLocaleString() : 'N/A'}</p>
                  </div>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/acquisitions/${acquisition.id}/detail`);
                  }}
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7 text-neutral-500 hover:text-neutral-900"
                >
                  <EyeIcon size={16} weight="duotone" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Bekijk details</p>
              </TooltipContent>
            </Tooltip>
            <DropdownMenu modal={false}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7 text-neutral-500 hover:text-neutral-900"
                    >
                      <DotsThreeVerticalIcon size={18} weight="bold" />
                    </Button>
                  </DropdownMenuTrigger>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Acties</p>
                </TooltipContent>
              </Tooltip>
              <DropdownMenuContent
                align="end"
                side="bottom"
                sideOffset={4}
                onClick={(e) => e.stopPropagation()}
                onCloseAutoFocus={(e) => e.preventDefault()}
                className="w-56"
              >
                <DropdownMenuLabel className="text-xs font-medium text-neutral-500">
                  Acties
                </DropdownMenuLabel>
                {acquisition.sharePointSpreadsheetUrl && (
                  <DropdownMenuItem
                    onClick={() => window.open(acquisition.sharePointSpreadsheetUrl, '_blank')}
                  >
                    <FileIcon size={16} weight="duotone" className="mr-2" />
                    <div className="flex flex-col">
                      <span>Open Excel</span>
                      {acquisition.lastUpdatedExcel && (
                        <span className="text-[10px] text-neutral-secondary-mid">
                          Bijgewerkt: {acquisition.lastUpdatedExcel}
                        </span>
                      )}
                    </div>
                  </DropdownMenuItem>
                )}
                {acquisition.sharePointFolderUrl && (
                  <DropdownMenuItem
                    onClick={() => window.open(acquisition.sharePointFolderUrl, '_blank')}
                  >
                    <FolderOpenIcon size={16} weight="duotone" className="mr-2" />
                    Open SharePoint folder
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={openNewDocumentGenerator}>
                  <FileTextIcon size={16} weight="duotone" className="mr-2" />
                  Genereer document (nieuw)
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => setArchiveDialogOpen(true)}
                  className="text-brick-primary focus:text-brick-primary"
                >
                  <ArchiveIcon size={16} weight="duotone" className="mr-2" />
                  Archiveren
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <AlertDialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
              <AlertDialogContent onClick={(e) => e.stopPropagation()}>
                <AlertDialogHeader>
                  <AlertDialogTitle>Ben je zeker?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Dit acquisitiedossier wordt gearchiveerd. Het blijft wel bestaan in de database.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Annuleren</AlertDialogCancel>
                  <AlertDialogAction onClick={() => archiveAcquisition()}>Archiveren</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>

        {/* Title & Address */}
        <div className="space-y-1">
          <h4 className="font-semibold text-sm text-neutral-900 line-clamp-2 leading-tight">
            {acquisition.name}
          </h4>
          <div className="flex items-start gap-1.5 text-xs text-neutral-secondary-mid">
            <MapPinIcon size={13} className="shrink-0 mt-0.5" weight="duotone" />
            <p className="line-clamp-1 leading-relaxed">
              {buildingAddresses}
            </p>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-2.5 p-3 pt-0">
        {/* Image - Using native img to avoid HeroUI Image loading state flashing */}
        <div
          onClick={(e) => e.stopPropagation()}
          className="relative w-full overflow-hidden rounded-md bg-neutral-100"
          style={{
            height: '140px',
            contain: 'layout style paint',
          }}
        >
          {coverImage ? (
            <img
              src={coverImage}
              alt={acquisition.name}
              className="h-full w-full object-cover"
              style={{
                opacity: 1,
                transition: 'none',
              }}
              loading="eager"
              decoding="async"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <div className="text-xs text-neutral-400">
                {imageLoading ? 'Laden...' : 'Geen afbeelding'}
              </div>
            </div>
          )}

          {/* Stats overlay — bottom-left of image, subtle like WhatsApp meta-chips */}
          <div className="absolute bottom-1.5 left-1.5 flex gap-1">
            <span className="flex items-center gap-1 rounded-full bg-black/55 px-1.5 py-0.5 text-[10px] font-medium text-white backdrop-blur-sm">
              <BuildingApartmentIcon size={11} weight="duotone" />
              {acquisition.buildings?.length || 0}
            </span>
            <span className="flex items-center gap-1 rounded-full bg-black/55 px-1.5 py-0.5 text-[10px] font-medium text-white backdrop-blur-sm">
              <UsersIcon size={11} weight="duotone" />
              {totalUnits}
            </span>
          </div>
        </div>

        {/* Pricing Info — compact */}
        <div className="space-y-1 px-2.5 py-2 rounded-md bg-neutral-50/60 border border-neutral-100 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-xs text-neutral-secondary-mid">Vraagprijs</span>
            <span className="font-semibold text-neutral-900">
              {applyMask(acquisition.grossAskingPrice, "valuta")}
            </span>
          </div>
          {acquisition.bidPriceActual && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-neutral-secondary-mid">Biedprijs</span>
              <span className="font-semibold text-moss-primary">
                {applyMask(acquisition.bidPriceActual, "valuta")}
              </span>
            </div>
          )}
          {acquisition.potentialGrossIncomeActual && (
            <div className="flex items-center justify-between pt-1.5 mt-1 border-t border-neutral-200/70">
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-xs text-neutral-secondary-mid cursor-help underline decoration-dotted">Bruto huur/jaar</span>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p>De bruto huur is de totale huurinkomsten per jaar, excl. doorrekenbare provisies en OV</p>
                </TooltipContent>
              </Tooltip>
              <div className="flex items-center gap-1">
                <span className="font-semibold text-neutral-900">
                  {applyMask(acquisition.potentialGrossIncomeActual, "valuta")}
                </span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-5 w-5"
                      disabled={isRefreshing}
                      onClick={(e) => {
                        e.stopPropagation();
                        refreshAcquisition();
                      }}
                    >
                      <RepeatIcon
                        size={12}
                        weight="duotone"
                        className={`text-moss-primary ${isRefreshing ? 'animate-spin' : ''}`}
                      />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{isRefreshing ? 'Aan het verversen...' : 'Ververs financiële gegevens'}</p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>
          )}
        </div>
      </CardContent>

      <CardFooter className="flex-col items-start space-y-1.5 p-3 pt-0">
        {/* Complete Acquisition Button for incomplete email imports */}
        {needsCompletion && onCompleteAcquisition && (
          <Button
            size="sm"
            className="w-full bg-moss-primary hover:bg-moss-primary/90 text-white"
            onClick={(e) => {
              e.stopPropagation();
              onCompleteAcquisition(acquisition);
            }}
          >
            <PencilSimpleIcon size={14} weight="duotone" className="mr-2" />
            Vervolledig dossier
          </Button>
        )}

        {/* Primary CTA: Generate document (modal-based) */}
        <div className="w-full" onClick={(e) => e.stopPropagation()}>
          <DocumentGeneratorComponent
            incomingDocumentCategory={DocumentCategory.ACQUISITION}
            incomingAcquisitionId={acquisition?.id}
            pictureSelection={true}
          />
        </div>

        {acquisition.lastUpdatedExcel && (
          <div className="flex items-center gap-1.5 text-[11px] text-neutral-secondary-mid w-full pt-0.5">
            <RepeatIcon size={11} weight="duotone" className="shrink-0" />
            <p className="truncate">Excel: {acquisition.lastUpdatedExcel}</p>
          </div>
        )}
      </CardFooter>
    </Card>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function for React.memo
  // Return true if props are equal (component should NOT re-render)
  // Return false if props are different (component SHOULD re-render)
  
  const prev = prevProps.acquisition;
  const next = nextProps.acquisition;
  
  // If highlighted state changed, re-render
  if (prevProps.isHighlighted !== nextProps.isHighlighted) {
    return false;
  }
  
  // If it's a different acquisition, re-render
  if (prev.id !== next.id) {
    return false;
  }
  
  // If phase changed, re-render
  if (prev.acquisitionPhase !== next.acquisitionPhase) {
    return false;
  }
  
  // Check key fields that would affect display
  // Only re-render if these actually changed
  if (
    prev.name !== next.name ||
    prev.grossAskingPrice !== next.grossAskingPrice ||
    prev.bidPriceActual !== next.bidPriceActual ||
    prev.potentialGrossIncomeActual !== next.potentialGrossIncomeActual ||
    prev.lastUpdatedExcel !== next.lastUpdatedExcel ||
    prev.dealType !== next.dealType ||
    prev.isEmailImport !== next.isEmailImport ||
    prev.yallyResponsibleUserId !== next.yallyResponsibleUserId
  ) {
    return false;
  }
  
  // If all checks pass, don't re-render (props are equal enough)
  return true;
});

AcquisitionCardInner.displayName = 'AcquisitionCardInner';

// Wrapper component that subscribes to contexts and passes data down
const AcquisitionCardWrapper: React.FC<AcquisitionCardProps> = ({
  acquisition,
  onClick,
  onUpdate,
  isHighlighted,
  onCompleteAcquisition
}) => {
  const { users } = useUsers();
  const { session } = useSession();

  // Memoize users and session to provide stable references
  const stableUsers = React.useMemo(() => users, [users.length]);
  const stableSession = React.useMemo(() => session, [session?.id]);

  return (
    <AcquisitionCardInner
      acquisition={acquisition}
      onClick={onClick}
      onUpdate={onUpdate}
      isHighlighted={isHighlighted}
      users={stableUsers}
      session={stableSession}
      onCompleteAcquisition={onCompleteAcquisition}
    />
  );
};

export const AcquisitionCard = React.memo(AcquisitionCardWrapper, (prevProps, nextProps) => {
  // Only re-render if acquisition ID or phase changes
  return (
    prevProps.acquisition.id === nextProps.acquisition.id &&
    prevProps.acquisition.acquisitionPhase === nextProps.acquisition.acquisitionPhase &&
    prevProps.isHighlighted === nextProps.isHighlighted
  );
});

